import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import Logo from '../components/Logo';
import API_URL from '../config/api';

const AdminSurveyTemplates = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchTemplates();
  }, [filterType]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const queryParams = filterType !== 'all' ? `?assessmentType=${filterType}` : '';
      const response = await axios.get(`${API_URL}/api/survey-templates${queryParams}`, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.templates) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load survey templates');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/survey-templates/${templateId}/activate`, {}, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      fetchTemplates(); // Refresh list
      alert('Template activated successfully');
    } catch (err) {
      console.error('Error activating template:', err);
      alert('Failed to activate template');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/survey-templates/${templateId}/set-default`, {}, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      fetchTemplates(); // Refresh list
      alert('Template set as default successfully');
    } catch (err) {
      console.error('Error setting default template:', err);
      alert('Failed to set default template');
    }
  };

  const handleClone = async (templateId) => {
    const name = prompt('Enter name for cloned template:');
    const version = prompt('Enter version for cloned template:', '1.0');

    if (!name || !version) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/survey-templates/${templateId}/clone`, {
        name,
        version
      }, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      fetchTemplates(); // Refresh list
      alert('Template cloned successfully');
    } catch (err) {
      console.error('Error cloning template:', err);
      alert('Failed to clone template');
    }
  };

  const handleDelete = async (templateId, template) => {
    if (template.isDefault && template.isActive) {
      alert('Cannot delete active default template. Set another template as default first.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/survey-templates/${templateId}`, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      fetchTemplates(); // Refresh list
      alert('Template deleted successfully');
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Logo />
          <button onClick={handleLogout} style={{
            backgroundColor: '#DC0032',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Logout
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading survey templates...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Logo />
        <div>
          <span style={{ marginRight: '20px' }}>Admin: {user?.fullName}</span>
          <button onClick={handleLogout} style={{
            backgroundColor: '#DC0032',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Logout
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ marginBottom: '20px' }}>
        <Link to="/admin" style={{ color: '#DC0032', textDecoration: 'none', marginRight: '10px' }}>← Back to Admin Dashboard</Link>
      </div>

      <h1 style={{ color: '#2D2D2D', marginBottom: '10px' }}>Survey Template Manager</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Manage survey templates, activate versions, and control which templates are used for assessments.
      </p>

      {/* Filter and Actions Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '5px'
      }}>
        <div>
          <label style={{ marginRight: '10px', color: '#2D2D2D', fontWeight: '500' }}>Filter by Type:</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}
          >
            <option value="all">All Types</option>
            <option value="dx">Digital Transformation</option>
            <option value="ai">AI Maturity</option>
          </select>
        </div>

        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '14px',
          border: '1px solid #ffeaa7'
        }}>
          Note: Only one template per type can be active at a time
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#FFF5F7',
          color: '#DC0032',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          border: '1px solid #DC0032'
        }}>
          {error}
        </div>
      )}

      {/* Templates Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: '#2D2D2D', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D', fontWeight: '600' }}>Type</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D', fontWeight: '600' }}>Version</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D', fontWeight: '600' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D', fontWeight: '600' }}>Questions</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D', fontWeight: '600' }}>Created</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.length > 0 ? (
              templates.map((template) => (
                <tr key={template._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px', color: '#2D2D2D' }}>
                    <div style={{ fontWeight: '500' }}>{template.name}</div>
                    {template.isDefault && (
                      <span style={{
                        fontSize: '11px',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        marginTop: '4px',
                        display: 'inline-block'
                      }}>
                        DEFAULT
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      backgroundColor: template.assessmentType === 'ai' ? '#7c3aed' : '#0ea5e9',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {template.assessmentType === 'ai' ? 'AI' : 'DX'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#666' }}>{template.version}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      backgroundColor: template.isActive ? '#28a745' : '#6c757d',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {template.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#2D2D2D' }}>{template.questions?.length || 0}</td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '13px' }}>
                    {new Date(template.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Link
                        to={`/admin/templates/${template._id}/edit`}
                        style={{
                          backgroundColor: '#6f42c1',
                          color: 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          textDecoration: 'none',
                          display: 'inline-block'
                        }}
                      >
                        Edit
                      </Link>
                      {!template.isActive && (
                        <button
                          onClick={() => handleActivate(template._id)}
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Activate
                        </button>
                      )}
                      {!template.isDefault && template.isActive && (
                        <button
                          onClick={() => handleSetDefault(template._id)}
                          style={{
                            backgroundColor: '#ffc107',
                            color: '#000',
                            border: 'none',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleClone(template._id)}
                        style={{
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Clone
                      </button>
                      <button
                        onClick={() => handleDelete(template._id, template)}
                        disabled={template.isDefault && template.isActive}
                        style={{
                          backgroundColor: template.isDefault && template.isActive ? '#e9ecef' : '#dc3545',
                          color: template.isDefault && template.isActive ? '#6c757d' : 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          cursor: template.isDefault && template.isActive ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No survey templates found. Templates should be seeded from the backend.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Help Text */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '5px',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{ color: '#2D2D2D', marginBottom: '15px' }}>How Template Management Works:</h3>
        <ul style={{ color: '#666', lineHeight: '1.8', paddingLeft: '20px' }}>
          <li><strong>Active Templates:</strong> Only active templates are used for assessments. When you activate a template, all other templates of the same type are automatically deactivated.</li>
          <li><strong>Default Templates:</strong> The default template is the primary version for that assessment type. Setting a template as default also activates it.</li>
          <li><strong>Cloning:</strong> Create a copy of an existing template to modify without affecting the original. Cloned templates start as inactive.</li>
          <li><strong>Deleting:</strong> You cannot delete an active default template. Set another template as default first, then delete.</li>
          <li><strong>Version Control:</strong> Use version numbers to track changes to your survey templates over time.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminSurveyTemplates;
